/**
 * Persists a sign-up plan selection across the register → login → first authenticated render
 * boundary. The pricing page links a visitor into `/signup?plan=<planId>`; we record the plan
 * locally so that after the new user successfully registers and the authenticated shell mounts,
 * the platform can take the user straight into a billing checkout for paid tiers (or skip it for
 * the free tier).
 *
 * Storage is intentionally `localStorage` and scoped by a single key so the value survives the
 * full-page reload that some SSO redirects trigger but is bounded and cleared after one use.
 */

const SIGNUP_INTENT_KEY = "aioscp.signup.intent";
const MAX_PLAN_ID_LENGTH = 128;

export interface SignupIntent {
  readonly planId: string;
  readonly recordedAt: string;
}

function isBrowser(): boolean {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function isValidPlanId(planId: unknown): planId is string {
  return (
    typeof planId === "string" &&
    planId.trim().length > 0 &&
    planId.length <= MAX_PLAN_ID_LENGTH &&
    /^[A-Za-z0-9_.:-]+$/.test(planId)
  );
}

export function recordSignupIntent(planId: string): boolean {
  if (!isBrowser()) return false;
  if (!isValidPlanId(planId)) return false;
  try {
    const intent: SignupIntent = { planId, recordedAt: new Date().toISOString() };
    window.localStorage.setItem(SIGNUP_INTENT_KEY, JSON.stringify(intent));
    return true;
  } catch {
    // localStorage may be unavailable (private mode, quota). Failing silently is correct here:
    // the post-signup checkout step is a convenience, not a correctness requirement.
    return false;
  }
}

export function consumeSignupIntent(): SignupIntent | null {
  if (!isBrowser()) return null;
  try {
    const raw = window.localStorage.getItem(SIGNUP_INTENT_KEY);
    if (!raw) return null;
    window.localStorage.removeItem(SIGNUP_INTENT_KEY);
    const parsed = JSON.parse(raw) as { planId?: unknown; recordedAt?: unknown };
    if (!isValidPlanId(parsed.planId)) return null;
    const recordedAt =
      typeof parsed.recordedAt === "string" ? parsed.recordedAt : new Date().toISOString();
    return { planId: parsed.planId, recordedAt };
  } catch {
    return null;
  }
}

export function peekSignupIntent(): SignupIntent | null {
  if (!isBrowser()) return null;
  try {
    const raw = window.localStorage.getItem(SIGNUP_INTENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { planId?: unknown; recordedAt?: unknown };
    if (!isValidPlanId(parsed.planId)) return null;
    const recordedAt =
      typeof parsed.recordedAt === "string" ? parsed.recordedAt : new Date().toISOString();
    return { planId: parsed.planId, recordedAt };
  } catch {
    return null;
  }
}

export function clearSignupIntent(): void {
  if (!isBrowser()) return;
  try {
    window.localStorage.removeItem(SIGNUP_INTENT_KEY);
  } catch {
    // ignore
  }
}
